// KYRS · PRIORITY — the #1 priority + top 3 open tasks, ranked the way KYRS ranks them.
//
// KYRS's real score is NOT a fixed Urgency/Impact pair: it is
// calculateTaskPriority (lib/autoSchedule.ts, mirrored by tools/KyrsTool.tsx
// calcPriority) = group number + subgroup numbers + the user's value-criterion
// scores (user_settings.value_settings — "Urgency", "Impact"… are whatever the
// user defined) + unscheduled bonus + manual priority_add. Higher = more
// important (the auto-scheduler sorts descending). We reuse the exported fn and
// the same "All active tasks" filter KyrsTool applies to its virtual view.
import { useMemo } from 'react';
import { supabase } from '../../../../lib/supabase';
import { calculateTaskPriority, parseETCToMinutes, type Group, type ValueSetting } from '../../../../lib/autoSchedule';
import { trunc } from '../../time';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction, useLoad } from './util';

const BASE = { id: 'kyrs', toolId: 'kyrs', orderFunction: 'direct', title: 'PRIORITY', deityName: 'KYRS', agentId: 'kyrs' } as const;

interface Ranked {
  id: string;
  title: string;
  score: number;
  etc: string | null;
  /** "Urgency Critical · Impact High" — resolved criterion picks (and group). */
  why: string[];
  unscored: boolean;
}
interface KyrsLoad {
  count: number;
  top: Ranked[]; // up to 3, highest score first
}

async function loadKyrs(userId: string): Promise<KyrsLoad> {
  const [listsRes, tasksRes, setRes] = await Promise.all([
    supabase.from('task_lists').select('id, include_in_all_tasks_view, category_settings').eq('user_id', userId),
    supabase.from('tasks').select('*').eq('user_id', userId).eq('done', false).limit(1000),
    supabase.from('user_settings').select('groups, value_settings, unscheduled_task_priority_bonus').eq('user_id', userId).maybeSingle(),
  ]);
  if (tasksRes.error) throw tasksRes.error;
  const lists = new Map<string, { inAll: boolean; cats: Record<string, { includeInAllTasks?: boolean }> }>();
  for (const l of (listsRes.data ?? []) as any[]) {
    lists.set(l.id, { inAll: l.include_in_all_tasks_view !== false, cats: l.category_settings ?? {} });
  }
  const s: any = setRes.data ?? {};
  const groups: Group[] = Array.isArray(s.groups) ? s.groups : [];
  const values: ValueSetting[] = Array.isArray(s.value_settings) ? s.value_settings : [];
  const bonus = typeof s.unscheduled_task_priority_bonus === 'number' ? s.unscheduled_task_priority_bonus : 0;

  const ranked: (Ranked & { order: number })[] = [];
  for (const r of (tasksRes.data ?? []) as any[]) {
    // Same filter as KyrsTool's "All active tasks" view.
    const list = lists.get(r.list_id);
    if (!list || !list.inAll) continue;
    if (r.tabled && !r.category) continue;
    if (r.category && (list.cats[r.category]?.includeInAllTasks ?? true) === false) continue;
    const input = {
      groupId: r.group_id ?? null,
      sg1Id: r.sg1_id ?? null,
      sg2Id: r.sg2_id ?? null,
      valueSelections: (r.value_selections ?? {}) as Record<string, string>,
      scheduled: r.scheduled ?? null,
      priorityAdd: r.priority_add ?? null,
    };
    const why: string[] = [];
    const g = groups.find((x) => x.id === input.groupId);
    if (g) why.push(g.title);
    for (const [vid, cid] of Object.entries(input.valueSelections)) {
      const vs = values.find((v) => v.id === vid && v.isActive);
      const crit = vs?.criteria.find((c) => c.id === cid);
      if (vs && crit) why.push(`${vs.name} ${crit.title}`);
    }
    ranked.push({
      id: r.id,
      title: r.title || 'Untitled task',
      score: calculateTaskPriority(input, groups, values, bonus),
      etc: r.etc ?? null,
      why,
      unscored: why.length === 0 && !input.priorityAdd,
      order: typeof r.order === 'number' ? r.order : Number.MAX_SAFE_INTEGER,
    });
  }
  ranked.sort((a, b) => b.score - a.score || a.order - b.order);
  return { count: ranked.length, top: ranked.slice(0, 3) };
}

export function useKyrsWidget(c: WidgetCtx): OrbWidgetModel {
  const uid = c.userId ?? null;
  const { value } = useLoad<KyrsLoad>(uid ? () => loadKyrs(uid) : null, [uid, c.tick]);

  return useMemo(() => {
    try {
      if (!value || value.top.length === 0) return emptyModel(BASE, c, 'No priorities set');
      const [first, second] = value.top;
      const tie = !!second && second.score === first.score;
      const missing = first.unscored;
      const attention = tie || missing;

      const lines = value.top.map((t, i) => {
        const mins = parseETCToMinutes(t.etc);
        const etc = mins != null ? `${Math.round(mins)}m` : t.etc;
        return `${i + 1}. ${trunc(t.title, 28)}   ${['P' + t.score, etc].filter(Boolean).join(' · ')}`;
      });
      if (first.why.length) lines.push(`#1 because: ${first.why.join(' · ')}`);

      const sfx = ` · ${value.count} priorities`;
      const summary = `#1 ${trunc(first.title, Math.max(8, 40 - 3 - sfx.length))}${sfx}`;
      const alert = missing
        ? 'Top task has no group or value scores'
        : tie
          ? `Tied at the top: ${trunc(first.title, 16)} / ${trunc(second.title, 16)}`
          : null;

      return {
        ...BASE,
        status: attention ? 'attention' : 'active',
        summary,
        metrics: [
          { label: 'Priorities', value: value.count },
          { label: 'Top score', value: first.score, tone: missing ? 'warning' : 'neutral' },
        ],
        alert,
        lines,
        primaryAction: { label: 'Choose next', run: () => c.openTool('kyrs', { initialTaskId: first.id }), tone: 'gold' },
        secondaryActions: [openAction(c, 'kyrs', 'Kyrs')],
        signals: {
          active: true,
          alerted: attention,
          related: !!c.step?.taskId && c.step.taskId === first.id,
        },
      } as OrbWidgetModel;
    } catch (e) {
      console.warn('[orb widget] kyrs model failed', e);
      return emptyModel(BASE, c, 'No priorities set');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, c.step?.taskId, c.openTool]);
}
